<template>
    <v-card>
        <v-card-title>
            {{$t('event_transport.items')}}
            <v-spacer></v-spacer>
            <v-btn v-if="isEditOrderMode" color="red" dark class="ml-3" @click="cancelEditOrder">
                {{$t('Cancel')}}
                <v-icon right>mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="isEditOrderMode" color="green" dark class="ml-3" @click="saveEditOrder">
                {{$t('Save')}}
                <v-icon right>mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initEditOrder">
                {{$t('Reorder')}}
                <v-icon right>mdi-swap-vertical</v-icon>
            </v-btn>
            <div class="button-spacer"></div>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initAddTransportItem">
                {{$t('add')}}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-data-table :headers="headers"
                      :items="tableTransports"
                      :loading="isTransportItemPending"
                      @pagination="currentPagination"
                      disable-sort>
            <template v-slot:item.scroller="{value}">
                <div v-show="isEditOrderMode" class="handle"><v-btn icon ><v-icon>drag_handle{{value}}</v-icon></v-btn></div>
                <div v-show="!isEditOrderMode"></div>
            </template>
            <template v-slot:item.name="{value,item}">
                <v-menu
                    v-if="!isEditOrderMode"
                    :ref="item.id.concat('name')"
                    :close-on-click="false"
                    :close-on-content-click="false"
                    :nudge-right="10"
                    transition="scale-transition"
                    >
                    <template v-slot:activator="{ on }">
                        <div @click="openInline(item)" v-on="on" style="cursor: pointer;">{{value}}</div>
                    </template>
                    <v-card>
                        <v-card-text>
                            <v-text-field outlined hide-details
                                :label="$t('transport_item.name')"
                                :placeholder="$t('transport_item.name')"
                                v-model="inlineEditingTransportItemList[item.id].name"></v-text-field>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-btn color="secondary" @click="cancelInline(item, item.id.concat('name'))">{{$t('cancel')}}</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" :loading="inlineSaving" @click="saveInlineItem(item, item.id.concat('name'))">{{$t('save')}}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <div v-if="isEditOrderMode">{{value}}</div>
            </template>
            <template v-slot:item.content="{value,item}">
                <v-menu
                    v-if="!isEditOrderMode"
                    :ref="item.id.concat('content')"
                    :close-on-click="false"
                    :close-on-content-click="false"
                    :nudge-right="10"
                    transition="scale-transition"
                    >
                    <template v-slot:activator="{ on }">
                        <div @click="openInline(item)" v-on="on" style="cursor: pointer;">
                            <froalaView v-model="value" class="popup-preview"></froalaView>
                        </div>
                    </template>
                    <v-card>
                        <v-card-title>
                            {{$t('transport_item.content')}}
                        </v-card-title>
                        <v-card-text>
                            <v-container class="popup-editor-container">
                                <froala
                                    v-if="showEditor"
                                    :config="froalaConfig"
                                    v-model="inlineEditingTransportItemList[item.id].content"
                                ></froala>
                            </v-container>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-btn color="secondary" @click="cancelInline(item, item.id.concat('content'))">{{$t('cancel')}}</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" :loading="inlineSaving" @click="saveInlineItem(item, item.id.concat('content'))">{{$t('save')}}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <div v-if="isEditOrderMode">
                    <froalaView v-model="value" class="popup-preview"></froalaView>
                </div>
            </template>
            <template v-slot:item.createdAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.updatedAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.actions="{item}">
                <v-btn color="primary" class="ma-2" :disabled="isEditOrderMode"
                       @click="initEditTransportItem(item)">
                    {{$t('edit')}}
                    <v-icon right>mdi-pencil</v-icon>
                </v-btn>
                <v-btn color="red" :dark="!isEditOrderMode" class="ma-2" :disabled="isEditOrderMode"
                       @click="deleteTransportItem(item)">
                    {{$t('delete')}}
                    <v-icon right>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="editTransportItemDialogShown"
                  max-width="600px"
                  persistent
                  scrollable
                  :retain-focus="false"
                  >
            <v-card>
                <v-card-title>
                    {{$t('transport_item.title')}}
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form v-model="isEditTransportItemFormValid">
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.order')"
                                                  :rules=[transportOrderRule]
                                                  :placeholder="$t('transport_item.order')"
                                                  v-model="editingTransportItem.order"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-file-input accept="image/*,.pdf"
                                                :label="$t('transport_item.picture')"
                                                v-model="editingTransportItem.picture"
                                                :append-outer-icon="editingTransportItem.picture ? 'mdi-open-in-new' : undefined"
                                                @click:append-outer="open(getFileUrl(editingTransportItem.picture), '_blank')">
                                        <template v-slot:selection="{file}">
                                            <span v-if="file['type'] && file['type'] == 'application/pdf' || getFileUrl(file).endsWith('.pdf')">{{file.name ? truncate(file.name) : truncate(getFileUrl(file))}}</span>
                                            <v-img :src="getFileUrl(file)"
                                                    max-width="250px"
                                                    max-height="250px" v-else></v-img>
                                        </template>
                                    </v-file-input>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-file-input accept="image/*,.pdf"
                                                :label="$t('transport_item.picture_tc')"
                                                v-model="editingTransportItem.picture_tc"
                                                :append-outer-icon="editingTransportItem.picture_tc ? 'mdi-open-in-new' : undefined"
                                                @click:append-outer="open(getFileUrl(editingTransportItem.picture_tc), '_blank')">
                                        <template v-slot:selection="{file}">
                                            <span v-if="file['type'] && file['type'] == 'application/pdf' || getFileUrl(file).endsWith('.pdf')">{{file.name ? truncate(file.name) : truncate(getFileUrl(file))}}</span>
                                            <v-img :src="getFileUrl(file)"
                                                    max-width="250px"
                                                    max-height="250px" v-else></v-img>
                                        </template>
                                    </v-file-input>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-file-input accept="image/*,.pdf"
                                                :label="$t('transport_item.picture_sc')"
                                                v-model="editingTransportItem.picture_sc"
                                                :append-outer-icon="editingTransportItem.picture_sc ? 'mdi-open-in-new' : undefined"
                                                @click:append-outer="open(getFileUrl(editingTransportItem.picture_sc), '_blank')">
                                        <template v-slot:selection="{file}">
                                            <span v-if="file['type'] && file['type'] == 'application/pdf' || getFileUrl(file).endsWith('.pdf')">{{file.name ? truncate(file.name) : truncate(getFileUrl(file))}}</span>
                                            <v-img :src="getFileUrl(file)"
                                                    max-width="250px"
                                                    max-height="250px" v-else></v-img>
                                        </template>
                                    </v-file-input>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.name')"
                                                  :placeholder="$t('transport_item.name')"
                                                  v-model="editingTransportItem.name"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.name_tc')"
                                                  :placeholder="$t('transport_item.name_tc')"
                                                  v-model="editingTransportItem.name_tc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.name_sc')"
                                                  :placeholder="$t('transport_item.name_sc')"
                                                  v-model="editingTransportItem.name_sc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.key')"
                                                  :placeholder="$t('transport_item.key')"
                                                  v-model="editingTransportItem.key"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.address')"
                                                  :placeholder="$t('transport_item.address')"
                                                  v-model="editingTransportItem.address"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.address_tc')"
                                                  :placeholder="$t('transport_item.address_tc')"
                                                  v-model="editingTransportItem.address_tc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.address_sc')"
                                                  :placeholder="$t('transport_item.address_sc')"
                                                  v-model="editingTransportItem.address_sc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                        :label="$t('transport_item.contact')"
                                        :placeholder="$t('transport_item.contact')"
                                        v-model="editingTransportItem.contact">
                                    </v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pt-0 q-pb-3 removeVMSG">
                                    <v-text-field outlined
                                                :label="$t('transport_item.map_link')"
                                                :placeholder="$t('transport_item.map_link')"
                                                v-model="editingTransportItem.map_link">
                                    </v-text-field>
                                    <span style="color: rgba(0, 0, 0, 0.5);font-size:12px;">
                                        Click <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank">here</a> to find the Google Place IDs.
                                    </span>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0 pt-4">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.map_link_tc')"
                                                  :placeholder="$t('transport_item.map_link_tc')"
                                                  v-model="editingTransportItem.map_link_tc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('transport_item.map_link_sc')"
                                                  :placeholder="$t('transport_item.map_link_sc')"
                                                  v-model="editingTransportItem.map_link_sc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0">{{$t('transport_item.content')}}</v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <froala
                                        v-if="showEditor"
                                        :config="froalaConfig"
                                        v-model="editingTransportItem.content"
                                    ></froala>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0">{{$t('transport_item.content_tc')}}</v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <froala
                                        v-if="showEditor"
                                        :config="froalaConfig"
                                        v-model="editingTransportItem.content_tc"
                                    ></froala>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0">{{$t('transport_item.content_sc')}}</v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <froala
                                        v-if="showEditor"
                                        :config="froalaConfig"
                                        v-model="editingTransportItem.content_sc"
                                    ></froala>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{$t('cancel')}}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveTransportItem"
                           :disabled="!isEditTransportItemFormValid">{{$t('save')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <PopupAlert 
            v-model="confirmCancelItemDialogShown"
            title="Warning"
            message="Do you wish to cancel your amendments?"
            :onYes="cancelEditTransportItem"
            :onNo="function() {confirmCancelItemDialogShown=false;}"
            ></PopupAlert>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-card>
</template>

<style scoped>
    .button-spacer {
        width: 10px;
    }
</style>

<script>
    import {computed, ref, onMounted, watch} from "@vue/composition-api";
    import {useFind} from "feathers-vuex";
    import {DateTime} from "luxon";
    import {cloneDeep} from "lodash";
    import Sortable from "sortablejs";
    import PopupAlert from "../../components/PopupAlert";
    import froalaConfig from "../../froala-config";

    export default {
        name: 'EventTransportItemList',
        props: {
            transportId: String,
        },
        components:{PopupAlert},
        setup(props, {root}) {
            const {TransportItem} = root.$FeathersVuex.api;

            const showEditor = ref(false);

            const transportItemParams = computed(() => {
                return {
                    query: {
                        fTransportId: props.transportId,
                        $sort: {order: 1},
                    }
                }
            })
            const transportItemQueryWhen = computed(() => {
                return !!props.transportId;
            });
            const {items: transportItems, isPending: isTransportItemPending} = useFind({
                model: TransportItem,
                params: transportItemParams,
                queryWhen: transportItemQueryWhen,
            });

            // Data Table
            const headers = ref([
                {text:'',value:'scroller',width:80},
                {text: root.$t('transport_item.order'), value: 'order'},
                {text: root.$t('transport_item.name'), value: 'name'},
                {text: root.$t('transport_item.content'), value: 'content'},
                {text: '', value: 'actions'},
            ]);

            const currentStart = ref(0);

            const currentPagination = function(e){
                currentStart.value = e.pageStart
            }
            // Add / Edit Transport Item
            const editTransportItemDialogShown = ref(false);
            const confirmCancelItemDialogShown = ref(false);
            const isEditTransportItemFormValid = ref(false);

            const editOrderTransport = ref([])
            const didOrderChange = ref(false);
            const isEditOrderMode = ref(false);
            const overlay = ref(false);
            const inlineSaving = ref(false);

            const editingTransportItem = ref(new TransportItem({fTransportId: props.transportId}));
            const inlineEditingTransportItemList = ref({})
            const initAddTransportItem = function () {
                editingTransportItem.value = new TransportItem({fTransportId: props.transportId});
                editingTransportItem.value.order = transportItems.value.length + 1
                editTransportItemDialogShown.value = true;
            };
            const initEditTransportItem = function (transportItem) {
                if(!isEditOrderMode.value){
                    editingTransportItem.value = cloneDeep(transportItem);
                    editTransportItemDialogShown.value = true;
                }
            };
            const deleteTransportItem = function (transportItem) {
                if(!isEditOrderMode.value){                
                    if (confirm(root.$t('transport_item.confirm_delete'))) {
                        transportItem.remove().then(() => {
                        }, root.$handleFeatherError.bind(root));
                    }
                }
            };
            const saveTransportItem = async function () {
                if( editingTransportItem.value.picture instanceof File){
                    editingTransportItem.value.picture = {src:await root.$uploadFile(editingTransportItem.value.picture)};
                }
                if (!editingTransportItem.value.picture) {
                    editingTransportItem.value.picture = null;
                }
                if( editingTransportItem.value.picture_tc instanceof File){
                    editingTransportItem.value.picture_tc = {src:await root.$uploadFile(editingTransportItem.value.picture_tc)};
                }
                if (!editingTransportItem.value.picture_tc) {
                    editingTransportItem.value.picture_tc = null;
                }
                if( editingTransportItem.value.picture_sc instanceof File){
                    editingTransportItem.value.picture_sc = {src:await root.$uploadFile(editingTransportItem.value.picture_sc)};
                }
                if (!editingTransportItem.value.picture_sc) {
                    editingTransportItem.value.picture_sc = null;
                }

                editingTransportItem.value.save().then(() => {
                    TransportItem.get(editingTransportItem.value.id);
                    editTransportItemDialogShown.value = false;
                    root.$store.dispatch('showSnackBar', root.$t('transport_item.save_successful'));
                }, root.$handleFeatherError.bind(root));
            };
            const cancelEditTransportItem = function(){
                editTransportItemDialogShown.value = false;
                confirmCancelItemDialogShown.value = false;
            }
            watch(transportItems,(transportItems,old)=>{
                const newList = {}
                for(let i=0;i<transportItems.length;i++){
                    newList[transportItems[i].id] = cloneDeep(transportItems[i])
                }
                inlineEditingTransportItemList.value = newList
            })

            const previewHtmlContentOption = {
                 modules: {
                    toolbar: false,
                },
                placeholder: '',
                theme: 'bubble',
            };
            const openInline = function(transportItem){
                inlineEditingTransportItemList.value[transportItem.id] = cloneDeep(transportItem)
            }
            const cancelInline = function(item, ref=null){
                if (ref) {
                    this.$refs[ref].isActive = false;
                }
            }
            const saveInlineItem = async function (item, ref=null){
                inlineSaving.value = true;
                await inlineEditingTransportItemList.value[item.id].save().then(()=>{
                    TransportItem.get(item.id).then(()=>{
                    });
                    root.$store.dispatch('showSnackBar', root.$t('transport_item.save_successful'));
                }, root.$handleFeatherError.bind(root));
                inlineSaving.value = false;
                if (ref) {
                    this.$refs[ref].isActive = false;
                }
            }
            
            const initEditOrder = function() {
                didOrderChange.value = false
                isEditOrderMode.value = true
                editOrderTransport.value = cloneDeep(sortedTransportItems.value);
            }
            const saveEditOrder = async function(){
                if(!didOrderChange.value){
                    isEditOrderMode.value = false
                    return;
                }

                overlay.value = true
                for(let i=0;i<editOrderTransport.value.length;i++){
                    await editOrderTransport.value[i].save().then(()=>{
                        TransportItem.get(editOrderTransport.value[i].id)
                    })
                }
                isEditOrderMode.value = false
                overlay.value = false;
            }

            const cancelEditOrder = function(){
                isEditOrderMode.value = false;
            }

            const quickSortTransportItems = function(sortTransportItems){
                if(sortTransportItems.length<2){
                    return sortTransportItems
                }

                const chosenIndex = sortTransportItems.length - 1
                const chosen  = sortTransportItems[chosenIndex]
                const a = []
                const b = []
                for(let i=0;i<chosenIndex;i++){
                    const temp = sortTransportItems[i].order
                    temp < chosen.order ? a.push(sortTransportItems[i]) : b.push(sortTransportItems[i])
                }
                const output = [...quickSortTransportItems(a),chosen,...quickSortTransportItems(b)]
                return output
            }

            const tableTransports = computed(()=>{
                return isEditOrderMode.value ? editOrderTransport.value : sortedTransportItems.value;
            })

            const sortedTransportItems = computed(()=>{
                return quickSortTransportItems(transportItems.value)
            })

            onMounted(() => {
                let tables = document.querySelectorAll('.v-data-table tbody');
                Sortable.create(tables[1],
                    {
                        handle:".handle", // Look for class with name "handle"
                        onEnd({newIndex,oldIndex}){
                            newIndex +=  currentStart.value;
                            oldIndex +=  currentStart.value;
                            const rowSelected = editOrderTransport.value.splice(oldIndex,1)[0];
                            editOrderTransport.value.splice(newIndex,0,rowSelected);

                            didOrderChange.value = true
                            for(let i=0;i<editOrderTransport.value.length;i++){
                                editOrderTransport.value[i].order = i+1
                            }
                            editOrderTransport.value = quickSortTransportItems(editOrderTransport.value);
                        }
                    }
                )
            });

            const transportOrderRule = function(value){
                const onlyNumberRule =  /^[0-9]+$/;
                return onlyNumberRule.test(value) || 'Invalid Number';
            }

            const truncate = (str) => {
                if (str.length > 35) {
                    return str.substr(0, 20) + '...' + str.substr(str.length - 10, str.length);
                } else {
                    return str;
                }
            }

            const open = (link, target) => {
                window.open(link, target);
            }

            return {
                transportItems,
                tableTransports,
                isTransportItemPending,
                inlineEditingTransportItemList,
                headers,

                editTransportItemDialogShown,
                confirmCancelItemDialogShown,
                initAddTransportItem,
                initEditTransportItem,
                deleteTransportItem,
                isEditTransportItemFormValid,
                editingTransportItem,
                saveTransportItem,
                cancelEditTransportItem,
                
                DateTime,

                previewHtmlContentOption,
                openInline,
                cancelInline,
                saveInlineItem,

                overlay,
                inlineSaving,
                isEditOrderMode,
                initEditOrder,
                saveEditOrder,
                cancelEditOrder,

                currentPagination,
                transportOrderRule,

                getFileUrl: root.$getFileURL,
                open,
                truncate,

                froalaConfig: {
                    ...froalaConfig,
                    toolbarSticky: false,
                    zIndex: 2000
                },
                showEditor,
            }
        },
        mounted() {
            setTimeout(() => {
               this.showEditor = true;
            }, 100);
        }
    }
</script>
<style>
    .removeVMSG .v-input .v-input__control .v-text-field__details{
       display: none;
    }
</style>
